import { useEffect, useState } from "react";
import { familyNotices, FAMILY_NOTICE_EVENT, dismissFamilyJoin, dismissFamilyDeletion } from "@/lib/family_notice";
import { UndoToast } from "@/features/common/undo_toast";
import { Link } from "react-router-dom";
import { useFamilyRemoval, type FamilyRemovalState } from "@/hooks/use_family_removal";
import { PrimaryButton, GhostButton, Row } from "@/features/common/primitives";
import { Sheet } from "@/features/common/sheet";
import { formatMoney } from "@/domain/money";
import type { LocalExpense } from "@/lib/types";

export function RemovedFamilyOffer({ state, heading = true }: { state: FamilyRemovalState; heading?: boolean }): React.JSX.Element | null {
  if (!state.removed) return null;
  if (state.removed.rows.length === 0) return <p>You're no longer in {state.previousFamily?.name ?? "the family"}</p>;
  const expenses = state.removed.rows
    .filter((entry) => entry.table === "expenses" && "deletedAt" in entry.row && entry.row.deletedAt === null)
    .map((entry) => entry.row as LocalExpense);
  const other = state.removed.rows.length - expenses.length;
  return (
    <div className="space-y-phi-0 py-phi-0">
      {heading ? <h2 className="text-phi-1 font-semibold">You're no longer in {state.previousFamily?.name ?? "the family"}</h2> : null}
      <p>
        {expenses.length} family {expenses.length === 1 ? "expense" : "expenses"} you logged hadn't uploaded yet.
      </p>
      {expenses.map((row) => (
        <Row key={row.id} className="border-t border-line">
          <span className="min-w-0 flex-1">
            {state.categories.find((category) => category.id === row.categoryId)?.name ?? "Family expense"}
            <small className="block text-tx-2">{new Date(row.occurredAt).toLocaleString()}</small>
          </span>
          <span className="tnum" data-money>
            {state.profile ? formatMoney(row.amountCents, state.profile.currency) : "—"}
          </span>
        </Row>
      ))}
      {other > 0 ? (
        <p className="text-tx-2">
          {other} other family changes haven't uploaded. Only expenses can be copied; the other changes will be
          discarded.
        </p>
      ) : null}
      {state.error ? (
        <p role="alert" className="text-over">
          {state.error}
        </p>
      ) : null}
      <PrimaryButton
        disabled={state.busy}
        onClick={() => {
          void state.resolve("copy");
        }}
      >
        Copy to my ledger
      </PrimaryButton>
      <button
        disabled={state.busy}
        className="min-h-[44px] w-full text-tx-2"
        onClick={() => {
          void state.resolve("discard");
        }}
      >
        Discard
      </button>
    </div>
  );
}
export function FamilyRemovedNotice(): React.JSX.Element | null {
  const state = useFamilyRemoval(true);
  if (!state.show)
    return state.notice ? (
      <p role="status" className="border-y border-line px-phi-1 py-phi-0 text-acc">
        {state.notice}
      </p>
    ) : null;
  return (
    <Sheet title={`You're no longer in ${state.previousFamily?.name ?? "the family"}`} onClose={state.dismiss}>
      {() => (
        <div className="px-phi-1">
          <RemovedFamilyOffer state={state} heading={false} />
          <GhostButton onClick={state.dismiss}>Not now</GhostButton>
          <Link
            className="flex min-h-[44px] items-center justify-center text-acc"
            to="/settings/family"
            onClick={state.dismiss}
          >
            Settings › Family
          </Link>
        </div>
      )}
    </Sheet>
  );
}

export function FamilyHomeNotices(): React.JSX.Element {
  const [notices, setNotices] = useState(familyNotices);
  useEffect(() => {
    const update = () => setNotices(familyNotices());
    window.addEventListener(FAMILY_NOTICE_EVENT, update);
    return () => window.removeEventListener(FAMILY_NOTICE_EVENT, update);
  }, []);
  return (
    <>
      {notices.deleted ? (
        <p role="status" className="border-y border-line px-phi-1 py-phi-0">
          {notices.deleted}
          <button className="min-h-[44px] px-phi-0 text-acc" onClick={dismissFamilyDeletion}>Dismiss</button>
        </p>
      ) : null}
      {notices.joined ? (
        <UndoToast message={notices.joined} actionLabel="OK" onUndo={dismissFamilyJoin} onDismiss={dismissFamilyJoin} />
      ) : null}
    </>
  );
}
